import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Ghost, Users, Dice5, Newspaper, Heart, Search, Image as ImageIcon, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { GufoIcon } from "@/components/GufoIcon";

const TONES = [
  { id: "dichiarazione", label: "📣 Dichiarazione ufficiale" },
  { id: "fiducia", label: "💪 Fiducia" },
  { id: "rabbia", label: "🔥 Rabbia" },
  { id: "ironia", label: "😏 Ironia" },
  { id: "disperazione", label: "😭 Disperazione" },
  { id: "resa", label: "🏳️ Resa" },
];

export default function ActionDock({ user, onGufi, onAction }) {
  const navigate = useNavigate();
  const [gufataOpen, setGufataOpen] = useState(false);
  const [pressOpen, setPressOpen] = useState(false);
  const [supporterOpen, setSupporterOpen] = useState(false);

  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [tone, setTone] = useState("dichiarazione");
  const [headline, setHeadline] = useState("");
  const [quote, setQuote] = useState("");
  const [pressImage, setPressImage] = useState(null);
  const [pressImagePreview, setPressImagePreview] = useState(null);
  const pressImageInput = useRef(null);

  const [supporters, setSupporters] = useState({ supporting: [], supported_by: [] });
  const [supSearch, setSupSearch] = useState("");

  useEffect(() => {
    if (gufataOpen || supporterOpen) {
      api.get("/public/teams").then(({ data }) => setTargets(data)).catch(() => {});
    }
    if (supporterOpen) {
      api.get("/me/supporters").then(({ data }) => setSupporters(data)).catch(() => {});
    }
  }, [gufataOpen, supporterOpen]);

  const availableTargets = useMemo(
    () => targets.filter((t) => t.user_id !== user?.id),
    [targets, user?.id]
  );

  const supportedIds = new Set((supporters.supporting || []).map((s) => s.target_user_id));

  const launchGufata = async () => {
    if (!target) return toast.error("Scegli una vittima 🎯");
    setBusy(true);
    try {
      const { data } = await api.post("/me/gufata", { target_user_id: target, message });
      toast.success("Gufata lanciata! Se la squadra perde: +2 Gufi", {
        description: `Wallet: ${data.gufi_wallet} Gufi · Giornata ${data.matchday}`,
      });
      onGufi && onGufi(data.gufi_wallet);
      onAction && onAction();
      setGufataOpen(false);
      setTarget(""); setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lancio gufata");
    } finally { setBusy(false); }
  };

  const onPressImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Serve un'immagine");
    if (file.size > 6 * 1024 * 1024) return toast.error("Immagine troppo grande (max 6 MB)");
    setPressImage(file);
    setPressImagePreview(URL.createObjectURL(file));
  };

  const clearPressImage = () => {
    setPressImage(null);
    if (pressImagePreview) URL.revokeObjectURL(pressImagePreview);
    setPressImagePreview(null);
    if (pressImageInput.current) pressImageInput.current.value = "";
  };

  const submitPress = async () => {
    if (headline.trim().length < 3) return toast.error("Aggiungi un titolo");
    if (quote.trim().length < 5) return toast.error("Aggiungi la tua dichiarazione");
    if (quote.length > 700) return toast.error("Il testo deve essere al massimo 700 caratteri");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("tone", tone);
      fd.append("headline", headline.trim());
      fd.append("quote", quote.trim());
      if (pressImage) fd.append("image", pressImage);
      const { data } = await api.post("/me/press-conference", fd);
      toast.success(`Conferenza pubblicata! +${data.reward} Gufi`, {
        description: `Wallet: ${data.gufi_wallet} Gufi`,
      });
      onGufi && onGufi(data.gufi_wallet);
      onAction && onAction();
      setPressOpen(false);
      setHeadline(""); setQuote(""); setTone("dichiarazione");
      clearPressImage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio conferenza");
    } finally { setBusy(false); }
  };

  const becomeSupporter = async (targetId) => {
    setBusy(true);
    try {
      await api.post("/me/supporter", { target_user_id: targetId });
      toast.success("Sei ora supporter di questa squadra! +1 Gufo al tuo beniamino.");
      const { data } = await api.get("/me/supporters");
      setSupporters(data);
      onAction && onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiunta supporter");
    } finally { setBusy(false); }
  };

  const ACTIONS = [
    { id: "conferenza", label: "Conferenza stampa", icon: Mic,   color: "var(--conferenza)", handler: () => setPressOpen(true) },
    { id: "gufata",     label: "Gufata",            icon: Ghost, color: "var(--gufata)",     handler: () => setGufataOpen(true) },
    { id: "supporter",  label: "Cerca supporter",   icon: Users, color: "var(--supporter)",  handler: () => setSupporterOpen(true) },
    { id: "scommetti",  label: "Fantascommetti",    icon: Dice5, color: "var(--scommetti)",  handler: () => navigate("/fantascommesse") },
  ];

  const captionFor = (id) => {
    if (id === "conferenza") return <>Ricevi <b className="inline-flex items-center gap-1"><GufoIcon size={12}/>+2</b> · 1 per giornata</>;
    if (id === "gufata")     return <>Costa <b className="inline-flex items-center gap-1"><GufoIcon size={12}/>1</b> · +2 se il target perde</>;
    if (id === "supporter")  return <>Ogni supporter dà <b className="inline-flex items-center gap-1"><GufoIcon size={12}/>+1</b> alla tua squadra</>;
    return "Quote 1X2 dinamiche · gratis";
  };

  const searched = availableTargets.filter((t) =>
    !supSearch || (t.team_name || "").toLowerCase().includes(supSearch.toLowerCase())
  );

  return (
    <>
      {/* Mobile bottom dock */}
      <div className="fixed left-3 right-3 z-40 md:hidden" style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
        <div className="glass rounded-2xl grid grid-cols-4 gap-1 p-2 shadow-2xl">
          {ACTIONS.map((a, i) => (
            <button key={a.id} data-testid={`action-${a.id}-mobile`} onClick={a.handler}
                    className="flex flex-col items-center justify-center py-2 rounded-xl hover:bg-slate-50 transition-colors pressable"
                    style={{ color: a.color, animationDelay: `${i * 60}ms` }}>
              <a.icon className="w-5 h-5" />
              <span className="text-[10px] mt-1 text-slate-700 leading-tight text-center px-1">{a.label.split(" ")[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:grid grid-cols-4 gap-3 stagger" data-testid="action-dock-desktop">
        {ACTIONS.map((a) => (
          <button key={a.id} data-testid={`action-${a.id}`} onClick={a.handler}
                  className="card-surface rounded-2xl p-4 text-left player-chip card-lift group"
                  style={{ borderColor: `${a.color}55` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                 style={{ background: `${a.color}22`, color: a.color }}>
              <a.icon className="w-5 h-5" />
            </div>
            <div className="display font-bold text-sm">{a.label}</div>
            <div className="text-xs text-slate-500 mt-1">{captionFor(a.id)}</div>
          </button>
        ))}
      </div>

      {/* Gufata dialog */}
      <Dialog open={gufataOpen} onOpenChange={setGufataOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="gufata-dialog">
          <DialogHeader>
            <DialogTitle className="display font-black text-2xl flex items-center gap-2">
              <Ghost className="w-6 h-6" style={{ color: "var(--gufata)" }} /> Lancia una gufata
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Costa <b className="inline-flex items-center gap-1 align-middle"><GufoIcon size={14}/>1</b>. Se la squadra bersaglio <b>perde</b> alla prossima giornata guadagni <b className="inline-flex items-center gap-1 align-middle"><GufoIcon size={14}/>+2</b>.
              Wallet: <b>{user?.gufi_wallet ?? 0}</b> Gufi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="gufata-target">
                <SelectValue placeholder="Scegli una squadra rivale" />
              </SelectTrigger>
              <SelectContent>
                {availableTargets.length === 0 && (
                  <div className="p-2 text-slate-500 text-sm">Nessuna squadra disponibile</div>
                )}
                {availableTargets.map((t) => (
                  <SelectItem key={t.user_id} value={t.user_id} data-testid={`gufata-target-${t.user_id}`}>
                    {t.team_name} · Girone {t.girone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea placeholder="Messaggio della gufata (opzionale)" value={message}
                      onChange={(e) => setMessage(e.target.value)} maxLength={140}
                      className="bg-slate-50 border-slate-200" data-testid="gufata-message" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setGufataOpen(false)} className="border-slate-300 text-slate-700 rounded-full">Annulla</Button>
              <Button onClick={launchGufata} disabled={busy} className="btn-brand rounded-full" data-testid="gufata-submit">
                {busy ? "Lancio..." : "Lancia gufata"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Press conference dialog */}
      <Dialog open={pressOpen} onOpenChange={setPressOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="press-dialog">
          <DialogHeader>
            <DialogTitle className="display font-black text-2xl flex items-center gap-2">
              <Newspaper className="w-6 h-6" style={{ color: "var(--conferenza)" }} /> Convoca conferenza stampa
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Massimo 1 conferenza per giornata. Pubblicando ricevi <b className="inline-flex items-center gap-1 align-middle"><GufoIcon size={14}/>+2</b>.
              Wallet: <b>{user?.gufi_wallet ?? 0}</b> Gufi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Tono</div>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="press-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`press-tone-${t.id}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Titolo</div>
              <Input placeholder="Es. 'Vinceremo il derby'" value={headline} maxLength={100}
                     onChange={(e) => setHeadline(e.target.value)}
                     className="bg-slate-50 border-slate-200" data-testid="press-headline" />
              <div className={`text-[10px] text-right mt-1 ${headline.length > 90 ? "text-orange-500" : "text-slate-400"}`}>{headline.length}/100</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Testo intervista</div>
              <Textarea placeholder="Quello che vuoi far sapere al mondo del Fantacalcio (max 700 caratteri)…"
                        value={quote} maxLength={700}
                        onChange={(e) => setQuote(e.target.value)}
                        className="bg-slate-50 border-slate-200 min-h-[160px]" data-testid="press-quote" />
              <div className={`text-[10px] text-right mt-1 ${quote.length > 650 ? "text-orange-500" : "text-slate-400"}`}>{quote.length}/700</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Immagine (opzionale)</div>
              {pressImagePreview ? (
                <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img src={pressImagePreview} alt="anteprima" className="w-full max-h-48 object-cover" data-testid="press-image-preview" />
                  <button type="button" onClick={clearPressImage} className="absolute top-2 right-2 rounded-full p-1.5 bg-black/60 hover:bg-black/80 text-white" data-testid="press-image-clear">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => pressImageInput.current?.click()}
                        className="flex items-center gap-2 w-full h-11 rounded-xl border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-4 text-slate-600 text-sm"
                        data-testid="press-image-btn">
                  <ImageIcon className="w-4 h-4" />
                  Aggiungi un'immagine (facoltativa)
                </button>
              )}
              <input ref={pressImageInput} type="file" accept="image/*" className="hidden" onChange={onPressImageChange} data-testid="press-image-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPressOpen(false)} className="border-slate-300 text-slate-700 rounded-full">Annulla</Button>
              <Button onClick={submitPress} disabled={busy} className="btn-brand rounded-full" data-testid="press-submit">
                {busy ? "Invio..." : "Convoca"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supporter dialog */}
      <Dialog open={supporterOpen} onOpenChange={setSupporterOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl w-[calc(100vw-1.5rem)] sm:w-auto p-4 sm:p-6" data-testid="supporter-dialog">
          <DialogHeader>
            <DialogTitle className="display font-black text-xl sm:text-2xl flex items-center gap-2">
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: "var(--supporter)" }} /> Cerca supporter
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs sm:text-sm">
              Ogni squadra che supporti riceve <b className="inline-flex items-center gap-1 align-middle"><GufoIcon size={14}/>+1</b>.
              Supporti <b>{supporters.count_supporting ?? supporters.supporting?.length ?? 0}</b> · Ti supportano <b>{supporters.count_supported_by ?? supporters.supported_by?.length ?? 0}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={supSearch} onChange={(e) => setSupSearch(e.target.value)}
                     placeholder="Cerca squadra da supportare…"
                     className="pl-9 bg-slate-50 border-slate-200" data-testid="supporter-search" />
            </div>
            <div className="max-h-[60vh] sm:max-h-[380px] overflow-y-auto space-y-2 pr-1">
              {searched.length === 0 && (
                <div className="text-slate-400 text-sm text-center py-6">Nessuna squadra trovata.</div>
              )}
              {searched.map((t) => {
                const already = supportedIds.has(t.user_id);
                return (
                  <div key={t.user_id} className="flex items-center gap-2 sm:gap-3 rounded-xl border border-slate-200 p-2.5 sm:p-3" data-testid={`supporter-row-${t.user_id}`}>
                    <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center display font-black text-slate-500 text-sm">
                      {t.girone}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate text-sm sm:text-base">{t.team_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Girone {t.girone}</div>
                    </div>
                    <Button
                      onClick={() => !already && becomeSupporter(t.user_id)}
                      disabled={already || busy}
                      size="sm"
                      className={`rounded-full shrink-0 h-9 px-3 sm:px-4 text-xs sm:text-sm ${already ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "btn-brand"}`}
                      data-testid={`supporter-btn-${t.user_id}`}
                    >
                      {already ? (
                        <><CheckCircle2 className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Attivo</span></>
                      ) : (
                        <><Heart className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Supporta</span><span className="sm:hidden">Segui</span></>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
